import * as styles from '@procore/core-css/src/components/_input.scss'
import { bind } from 'classnames/bind'
import * as React from 'react'
import { useAutofocus } from '../effects/Autofocus'
import { useHotkey } from '../effects/Hotkey'
import IconLayout, { Icons } from '../layouts/IconLayout'
import { TextEntryApi, TextEntryConfig, useTextEntry } from '../state/TextEntry'
import { Omit } from '../utils/types'
import Icon, { IconVariantNS } from './Icon'
import Input, { InputProps } from './Input'

export interface SearchConfig extends Omit<TextEntryConfig, 'onChange'> {
  onChange?: TextEntryConfig['onChange']
  onClear?: () => void
  onSubmit?: (value: string) => void
  ref: React.RefObject<HTMLInputElement>
}

export interface SearchProps extends InputProps, Omit<SearchConfig, 'ref'> {
  autoFocus?: boolean
  empty?: boolean
  showSearch?: boolean
  typeahead?: boolean
}

const cx = bind(styles)

export interface SearchApi extends TextEntryApi {
  submit: () => void
}

// TODO: decouple Autocomplete concerns
export const useSearch = ({
  initialValue = '',
  onChange = event => {},
  onClear = () => {},
  onSubmit = (value: string) => {},
  ref,
}: SearchConfig) => {
  const textEntry = useTextEntry({ initialValue, onChange })

  const clear = React.useCallback(() => {
    textEntry.clear()
    onClear()
    ref.current && ref.current.focus()
  }, [])

  const submit = React.useCallback(() => {
    onSubmit(textEntry.value)
  }, [])

  useHotkey({ key: 'Enter', handler: submit, scope: ref })

  return { ...textEntry, clear, submit }
}

export const SearchView = React.forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      autoFocus = false,

      onChange = (event: React.ChangeEvent<HTMLInputElement>) => {},
      onClear = () => {},
      onSubmit = (value: string) => {},
      showSearch = true,
      typeahead = false,
      value = '',
      empty = !value,
      ...props
    },
    ref
  ) => {
    const inputRef =
      (ref as React.RefObject<HTMLInputElement>) ||
      React.createRef<HTMLInputElement>()

    useAutofocus({ enabled: autoFocus, ref: inputRef })

    return (
      <IconLayout count={showSearch ? 2 : 1}>
        <Input {...props} ref={inputRef} onChange={onChange} value={value} />
        <Icons className={cx('core-input-icons')}>
          {!empty && (
            <Icon
              clickable
              icon="x"
              onClick={onClear}
              variant={IconVariantNS.hoverCircle}
            />
          )}
          {showSearch && (
            <Icon
              clickable={!typeahead}
              icon="search"
              onClick={() => onSubmit(value)}
              variant={typeahead ? IconVariantNS.none : IconVariantNS.hoverFill}
            />
          )}
        </Icons>
      </IconLayout>
    )
  }
)

export const Search = React.forwardRef<
  HTMLInputElement,
  InputProps & SearchProps
>(
  (
    { onChange, onClear, onSubmit, value = '', initialValue = value, ...props },
    ref
  ) => {
    const refSearch =
      (ref as React.RefObject<HTMLInputElement>) ||
      React.createRef<HTMLInputElement>()

    const search = useSearch({
      initialValue,
      onChange,
      onClear,
      onSubmit,
      ref: refSearch,
    })

    const showSearch = !props.typeahead || (props.typeahead && search.empty)

    return (
      <SearchView
        {...{
          ...props,
          empty: search.empty,
          onChange: search.onChange,
          onClear: search.clear,
          onSubmit: search.submit,
          ref,
          showSearch,
          value: search.value,
        }}
      />
    )
  }
)

Search.displayName = 'Search'

export default Search
